/**
 * Shared credentials types, typed errors, and pure parse/validate logic.
 * No I/O — file reading stays at the boundary (interface/cli, interface/acp).
 */

// ─── Typed errors ─────────────────────────────────────────────────────────────

export class CredentialsNotFoundError extends Error {
  readonly filePath: string;
  constructor(filePath: string, cause?: unknown) {
    super(
      `Credentials file not found: ${filePath}. Run "pineseed login" to authenticate.`,
    );
    this.name = "CredentialsNotFoundError";
    this.filePath = filePath;
    if (cause) this.cause = cause;
  }
}

export class CredentialsExpiredError extends Error {
  readonly filePath: string;
  readonly expiredAt: Date;
  constructor(filePath: string, expiredAt: Date) {
    super(
      `Credentials in ${filePath} expired at ${expiredAt.toISOString()}. Run "pineseed login" to re-authenticate.`,
    );
    this.name = "CredentialsExpiredError";
    this.filePath = filePath;
    this.expiredAt = expiredAt;
  }
}

// ─── Credentials shape ────────────────────────────────────────────────────────

export interface Credentials {
  apiKey: string;
  /** TTL in seconds as returned by the auth server. */
  expiresIn: number;
  /** Unix ms timestamp of when the credentials were issued (written by login command). */
  issuedAt: number;
}

// ─── Pure parse + validate + expiry check (no I/O) ───────────────────────────

export function parseCredentials(raw: string, filePath: string): Credentials {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(`Credentials file at ${filePath} is not valid JSON`);
  }

  if (typeof parsed !== "object" || parsed === null) {
    throw new Error(`Credentials file at ${filePath} must be a JSON object`);
  }

  const p = parsed as Record<string, unknown>;

  if (typeof p["apiKey"] !== "string" || !p["apiKey"]) {
    throw new Error(
      `Credentials file at ${filePath} must contain a non-empty "apiKey" string`,
    );
  }
  if (typeof p["expiresIn"] !== "number") {
    throw new Error(
      `Credentials file at ${filePath} must contain a numeric "expiresIn" field`,
    );
  }
  if (typeof p["issuedAt"] !== "number") {
    throw new Error(
      `Credentials file at ${filePath} must contain a numeric "issuedAt" field. Re-run "pineseed login" to refresh.`,
    );
  }

  const expiredAt = new Date(
    (p["issuedAt"] as number) + (p["expiresIn"] as number) * 1000,
  );
  if (Date.now() > expiredAt.getTime()) {
    throw new CredentialsExpiredError(filePath, expiredAt);
  }

  return p as unknown as Credentials;
}
